import { canonicalProvisioningContext, decryptProvisioningPayload, deriveProvisioningKey, generateProductionDeviceIdentity, importAgreementPublicKeySpki, importProvisionedRoomKey, signPairingCompletion, type CompletionChallenge, type ProductionDeviceIdentity, type ProvisioningContext } from "@echopaste/crypto";
import { createProductionInvitation, parseProductionInvitation, PROTOCOL_VERSION, type DevicePublicIdentity, type KeyProvisioningEnvelope, type PairingRequest } from "@echopaste/protocol";
import { derivePairingSas, encryptProvisioningPayload, generateInvitationSecret, invitationSecretVerifier, uint8ArrayToBase64Url } from "@echopaste/crypto";
import { activateProductionRoom, loadProductionDeviceIdentity, loadProductionRoom, savePendingProductionRoom, saveProductionDeviceIdentity, unwrapProductionRoomKey } from "./storage/db";
import { signedProductionFetch } from "./productionAuth";

export interface InviterPublicIdentity { deviceId: string; signingFingerprint: string; agreementFingerprint: string; agreementPublicKeySpki: string; }
export interface PairingSession { invitationId: string; invitationSecret: string; requestId: string; local: ProductionDeviceIdentity; inviterDevice: InviterPublicIdentity; sas: string; completionRoomId?: string; }
const json = async (response: Response) => { const value = await response.json() as any; if (!response.ok || !value.ok) throw new Error(value.error || "Production pairing request failed"); return value; };
const localIdentity = async (): Promise<ProductionDeviceIdentity> => { const stored = await loadProductionDeviceIdentity(); if (stored) return stored; const created = await generateProductionDeviceIdentity(); await saveProductionDeviceIdentity(created); return created; };
const existingLocalIdentity = async (): Promise<ProductionDeviceIdentity> => {
  const stored = await loadProductionDeviceIdentity();
  if (!stored) throw new Error("Production device identity is required before creating an invitation.");
  return stored;
};
const publicIdentity = (local: ProductionDeviceIdentity): DevicePublicIdentity => ({ deviceId: local.deviceId, signingPublicKeySpki: local.signingPublicKeySpki, signingFingerprint: local.signingFingerprint, agreementPublicKeySpki: local.agreementPublicKeySpki, agreementFingerprint: local.agreementFingerprint, platform: "web" });

export async function startJoinerPairing(apiBase: string, ep2: string): Promise<PairingSession> {
  const parsed = parseProductionInvitation(ep2); const local = await localIdentity(); const requestId = crypto.randomUUID();
  // Stay strictly inside the inviter's 10-minute window even after network and UI delay.
  const response = await fetch(`${apiBase}/api/v1/production/pairing/requests`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ requestId, invitationId: parsed.invitationId, verifier: await invitationSecretVerifier(parsed.secret), joiningDevice: publicIdentity(local), expiresAt: new Date(Date.now() + 9 * 60_000).toISOString() }) });
  const body = await json(response); const inviterDevice = body.inviterDevice as InviterPublicIdentity;
  const sas = await derivePairingSas({ invitationId: parsed.invitationId, requestId, inviterSigningFingerprint: inviterDevice.signingFingerprint, inviterAgreementFingerprint: inviterDevice.agreementFingerprint, joinerSigningFingerprint: local.signingFingerprint, joinerAgreementFingerprint: local.agreementFingerprint, invitationMaterial: await invitationSecretVerifier(parsed.secret) });
  return { invitationId: parsed.invitationId, invitationSecret: parsed.secret, requestId, local, inviterDevice, sas };
}

export type ProvisioningCheckResult = { status: "pending" } | { status: "completed"; roomId: string };

export async function checkProvisioningAndComplete(apiBase: string, session: PairingSession): Promise<ProvisioningCheckResult> {
  if (session.completionRoomId) {
    await completePendingProductionPairing(apiBase, session.completionRoomId, session.requestId, session.invitationId, session.local);
    return { status: "completed", roomId: session.completionRoomId };
  }
  const response = await fetch(`${apiBase}/api/v1/production/pairing/provisioning/${encodeURIComponent(session.requestId)}?deviceId=${encodeURIComponent(session.local.deviceId)}`);
  if (response.status === 404) return { status: "pending" };
  const body = await json(response);
  await decryptAndPersistPendingProvisioning(body.envelope as KeyProvisioningEnvelope, session.requestId, session.invitationId, session.local, session.inviterDevice);
  session.completionRoomId = (body.envelope as KeyProvisioningEnvelope).roomId;
  await completePendingProductionPairing(apiBase, session.completionRoomId, session.requestId, session.invitationId, session.local);
  return { status: "completed", roomId: session.completionRoomId };
}

export interface InviterSession { invitationId: string; invitationSecret: string; invitationCode: string; roomId: string; local: ProductionDeviceIdentity; expiresAt: string; }
export async function createProductionPairingInvitation(apiBase: string, roomId: string): Promise<InviterSession> {
  const local = await existingLocalIdentity();
  const room = await loadProductionRoom(roomId);
  if (!room || room.state !== "active") throw new Error("Production pairing requires an existing authorized production room/device.");
  const invitationId = crypto.randomUUID();
  const invitationSecret = generateInvitationSecret();
  const expiresAt = new Date(Date.now() + 10 * 60_000).toISOString();
  const rawBody = JSON.stringify({
    invitationId,
    roomId,
    creatorDeviceId: local.deviceId,
    verifier: await invitationSecretVerifier(invitationSecret),
    expiresAt,
  });
  const res = await signedProductionFetch({
    apiBase,
    identity: local,
    roomId,
    path: "/api/v1/production/pairing/invitations",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: rawBody,
  });
  await json(res);
  return { invitationId, invitationSecret, invitationCode: createProductionInvitation(invitationId, invitationSecret), roomId, local, expiresAt };
}

/** Maps the intentionally raw D1-backed edge response at this client boundary only. */
function mapEdgePairingRequest(raw: unknown): PairingRequest | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const required = ["request_id", "invitation_id", "joining_device_id", "signing_public_key", "signing_fingerprint", "agreement_public_key", "agreement_fingerprint", "created_at", "expires_at"];
  if (!required.every((name) => typeof row[name] === "string" && row[name].trim().length > 0) || row.state !== "pending" || Number.isNaN(Date.parse(row.created_at as string)) || Number.isNaN(Date.parse(row.expires_at as string))) return null;
  if (row.display_name !== null && row.display_name !== undefined && (typeof row.display_name !== "string" || !row.display_name.trim())) return null;
  if (row.platform !== null && row.platform !== undefined && !["windows", "android", "web", "ios-pwa"].includes(row.platform as string)) return null;
  return {
    requestId: row.request_id as string,
    invitationId: row.invitation_id as string,
    joiningDevice: {
      deviceId: row.joining_device_id as string,
      signingPublicKeySpki: row.signing_public_key as string,
      signingFingerprint: row.signing_fingerprint as string,
      agreementPublicKeySpki: row.agreement_public_key as string,
      agreementFingerprint: row.agreement_fingerprint as string,
      ...(typeof row.display_name === "string" ? { displayName: row.display_name } : {}),
      ...(typeof row.platform === "string" ? { platform: row.platform as DevicePublicIdentity["platform"] } : {}),
    },
    createdAt: row.created_at as string,
    expiresAt: row.expires_at as string,
    state: "pending",
  };
}

export async function getPendingProductionRequest(apiBase: string, session: InviterSession): Promise<PairingRequest | null> {
  const path = `/api/v1/production/pairing/invitations/${encodeURIComponent(session.invitationId)}/requests`;
  const res = await signedProductionFetch({
    apiBase,
    identity: session.local,
    roomId: session.roomId,
    path,
    method: "GET",
  });
  const body = await json(res);
  const requests = Array.isArray(body.requests) ? body.requests : [];
  const mapped = requests.map((raw: unknown) => mapEdgePairingRequest(raw));
  return mapped.find((request: PairingRequest | null): request is PairingRequest => request?.invitationId === session.invitationId) ?? null;
}
export async function deriveInviterSas(session: InviterSession, request: PairingRequest): Promise<string> { const j = request.joiningDevice; return derivePairingSas({ invitationId: session.invitationId, requestId: request.requestId, inviterSigningFingerprint: session.local.signingFingerprint, inviterAgreementFingerprint: session.local.agreementFingerprint, joinerSigningFingerprint: j.signingFingerprint, joinerAgreementFingerprint: j.agreementFingerprint, invitationMaterial: await invitationSecretVerifier(session.invitationSecret) }); }
export async function approveAndProvision(apiBase: string, session: InviterSession, request: PairingRequest): Promise<void> {
  const approvePath = `/api/v1/production/pairing/requests/${encodeURIComponent(request.requestId)}/approve`;
  const approveRes = await signedProductionFetch({
    apiBase,
    identity: session.local,
    roomId: session.roomId,
    path: approvePath,
    method: "POST",
  });
  await json(approveRes);

  const room = await loadProductionRoom(session.roomId);
  if (!room || room.state !== "active") throw new Error("Active production room required");
  const rawRoomKeyMaterial = await unwrapProductionRoomKey(room);
  const joiner = request.joiningDevice;
  const context: ProvisioningContext = {
    invitationId: session.invitationId,
    requestId: request.requestId,
    roomId: session.roomId,
    keyVersion: room.keyVersion,
    senderDeviceId: session.local.deviceId,
    recipientDeviceId: joiner.deviceId,
    senderAgreementFingerprint: session.local.agreementFingerprint,
    recipientAgreementFingerprint: joiner.agreementFingerprint,
  };
  const expiresAt = new Date(Date.now() + 10 * 60_000).toISOString();
  const contextText = canonicalProvisioningContext(context);
  const salt = uint8ArrayToBase64Url(crypto.getRandomValues(new Uint8Array(32)));
  const peer = await importAgreementPublicKeySpki(joiner.agreementPublicKeySpki);
  const key = await deriveProvisioningKey(session.local.agreementPrivateKey, peer, salt, context);
  const encrypted = await encryptProvisioningPayload(JSON.stringify({ ...context, expiresAt, roomKeyMaterial: rawRoomKeyMaterial }), key, contextText);
  const envelope: KeyProvisioningEnvelope = {
    provisioningId: request.requestId,
    ...context,
    encryptedPayload: encrypted.ciphertext,
    nonce: encrypted.nonce,
    salt,
    context: contextText,
    expiresAt,
    version: PROTOCOL_VERSION,
  };
  const envelopeJson = JSON.stringify(envelope);
  const provisionPath = `/api/v1/production/pairing/requests/${encodeURIComponent(request.requestId)}/provision`;
  const provisionRes = await signedProductionFetch({
    apiBase,
    identity: session.local,
    roomId: session.roomId,
    path: provisionPath,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: envelopeJson,
  });
  await json(provisionRes);
}

/** Rejects every server-visible binding mismatch before ECDH/decryption. */
export function validateProvisioningEnvelope(envelope: KeyProvisioningEnvelope, requestId: string, invitationId: string, local: ProductionDeviceIdentity, inviter: InviterPublicIdentity): ProvisioningContext {
  if (envelope.provisioningId !== requestId || envelope.requestId !== requestId || envelope.invitationId !== invitationId || envelope.recipientDeviceId !== local.deviceId || envelope.recipientAgreementFingerprint !== local.agreementFingerprint || envelope.senderDeviceId !== inviter.deviceId || envelope.senderAgreementFingerprint !== inviter.agreementFingerprint || Date.parse(envelope.expiresAt) <= Date.now()) throw new Error("Provisioning envelope binding mismatch");
  const context = { invitationId, requestId, roomId: envelope.roomId, keyVersion: envelope.keyVersion, senderDeviceId: inviter.deviceId, recipientDeviceId: local.deviceId, senderAgreementFingerprint: inviter.agreementFingerprint, recipientAgreementFingerprint: local.agreementFingerprint };
  if (envelope.context !== canonicalProvisioningContext(context)) throw new Error("Provisioning context mismatch");
  return context;
}

export async function decryptAndPersistPendingProvisioning(envelope: KeyProvisioningEnvelope, requestId: string, invitationId: string, local: ProductionDeviceIdentity, inviter: InviterPublicIdentity): Promise<void> {
  const context = validateProvisioningEnvelope(envelope, requestId, invitationId, local, inviter);
  const peer = await importAgreementPublicKeySpki(inviter.agreementPublicKeySpki);
  const wrappingKey = await deriveProvisioningKey(local.agreementPrivateKey, peer, envelope.salt, context);
  const plaintext = await decryptProvisioningPayload({ ciphertext: envelope.encryptedPayload, nonce: envelope.nonce }, wrappingKey, envelope.context);
  const payload = JSON.parse(plaintext) as Record<string, unknown>;
  for (const [key, value] of Object.entries({ ...context, expiresAt: envelope.expiresAt })) if (payload[key] !== value) throw new Error("Provisioning payload binding mismatch");
  if (typeof payload.roomKeyMaterial !== "string") throw new Error("Provisioning payload missing room key material");
  await importProvisionedRoomKey(payload.roomKeyMaterial); // validates import; raw material then only reaches local wrapping.
  await savePendingProductionRoom({ roomId: context.roomId, keyVersion: context.keyVersion, rawRoomKeyMaterial: payload.roomKeyMaterial });
}

export async function completePendingProductionPairing(apiBase: string, roomId: string, requestId: string, invitationId: string, local: ProductionDeviceIdentity): Promise<void> {
  const challenge: CompletionChallenge = { requestId, invitationId, provisioningId: requestId, deviceId: local.deviceId, nonce: crypto.randomUUID(), expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(), version: 1 };
  const signature = await signPairingCompletion(local.signingPrivateKey, challenge);
  const response = await fetch(`${apiBase}/api/v1/production/pairing/provisioning/${encodeURIComponent(requestId)}/complete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challenge, signature, signedAt: new Date().toISOString(), version: 1 }) });
  if (!response.ok) throw new Error("Pairing completion rejected");
  await activateProductionRoom(roomId);
}
